/**
 * PDP gallery layout nudge: dispatch synthetic `resize` events so galleries that
 * measure on resize recalculate after render (e.g. third-party / sticky layouts).
 *
 * History: an earlier implementation fired 22 resize events every 500ms over the
 * first 11s of every page load (and every section:load). Splide listens to
 * `window.resize` and re-runs its layout pass each time, which interrupts
 * in-flight `.go(index)` animations and clears its user-gesture flag, making
 * user thumbnail clicks land on a busy carousel and require a second click to
 * activate. This implementation drops the burst and fires resize only at
 * targeted moments (DOM settle + LPO mount + section reload).
 */
(function () {
  var LOG_PREFIX = '[FT PDP gallery resize]';
  var DOM_SETTLE_MS = 300;
  var LPO_MOUNT_SETTLE_MS = 500;
  var LPO_MOUNT_OBSERVER_TIMEOUT_MS = 10000;
  var SECTION_LOAD_SETTLE_MS = 300;
  var LPO_WRAPPER_SELECTOR = '.cl-po--gallery-wrapper';

  /**
   * Fire a single synthetic resize event so Splide / sticky layouts recompute.
   * @param {string} source short label for telemetry / debugging.
   */
  function dispatchResize(source) {
    window.dispatchEvent(
      new UIEvent('resize', {
        view: window,
        bubbles: false,
        cancelable: false,
      })
    );
    console.log(LOG_PREFIX, 'dispatch', '(' + source + ')');
  }

  /**
   * Schedule a single resize after a short delay so Splide measures after the
   * surrounding DOM has settled. Each call is independent, so multiple sources
   * can stack their own delayed nudges without interfering.
   * @param {string} source
   * @param {number} delayMs
   */
  function scheduleSingle(source, delayMs) {
    setTimeout(function () {
      dispatchResize(source);
    }, delayMs);
  }

  function isPdpContext() {
    return !!document.querySelector(
      'script[type="application/json"][id^="ProductJson-"]'
    );
  }

  function isProductInformationSectionLoad(/** @type {Event} */ ev) {
    var t = ev && ev.target;
    return t instanceof Element && !!t.querySelector('.product-information');
  }

  /**
   * Watch for LPO's gallery wrapper to appear in the DOM (it mounts
   * asynchronously after the Cloudlift script loads). When it shows up, fire
   * two resizes: one immediately (Splide has just measured), one after a short
   * settle window (after Splide hydrates lazy images and re-clones slides).
   * After firing, the observer disconnects so user clicks are never
   * interrupted by further synthetic resizes.
   */
  function watchForLpoMount() {
    // Already mounted? Just fire and exit.
    if (document.querySelector(LPO_WRAPPER_SELECTOR)) {
      console.log(LOG_PREFIX, 'LPO wrapper already present at watch start');
      dispatchResize('lpo-mount');
      scheduleSingle('lpo-mount-settle', LPO_MOUNT_SETTLE_MS);
      return;
    }

    var fired = false;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    var giveUpTimer;
    var observer = new MutationObserver(function () {
      if (fired) return;
      if (!document.querySelector(LPO_WRAPPER_SELECTOR)) return;
      fired = true;
      observer.disconnect();
      if (giveUpTimer !== undefined) clearTimeout(giveUpTimer);
      console.log(LOG_PREFIX, 'LPO wrapper detected');
      dispatchResize('lpo-mount');
      scheduleSingle('lpo-mount-settle', LPO_MOUNT_SETTLE_MS);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Failsafe: stop watching after a generous window so we never leak a
    // page-wide subtree observer if LPO never mounts (e.g. app uninstalled).
    giveUpTimer = setTimeout(function () {
      if (fired) return;
      observer.disconnect();
      console.log(LOG_PREFIX, 'LPO mount observer timed out');
    }, LPO_MOUNT_OBSERVER_TIMEOUT_MS);
  }

  function onReady() {
    if (!isPdpContext()) return;
    scheduleSingle('dom-settle', DOM_SETTLE_MS);
    watchForLpoMount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    if (!isPdpContext()) return;
    if (!isProductInformationSectionLoad(ev)) return;
    scheduleSingle('section:load', SECTION_LOAD_SETTLE_MS);
  });
})();

 
// ✅ Universal Cloudlift preview refresh — 3 runs, every 2s after page load
// Works for ALL products on the page — no hardcoded IDs needed

function refreshCloudliftPreviewInputs() {
  const inputs = document.querySelectorAll(
    '.cl-po--input[type="text"], textarea.cl-po--input'
  );
  inputs.forEach((input) => {
    if (
      !(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) ||
      !input.value
    ) {
      return;
    }
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
  });
}

const CL_REFRESH_RUNS = 3;
const CL_REFRESH_INTERVAL_MS = 2000;

function scheduleCloudliftPreviewRefresh() {
  for (let i = 0; i < CL_REFRESH_RUNS; i++) {
    setTimeout(() => {
      refreshCloudliftPreviewInputs();
    }, (i + 1) * CL_REFRESH_INTERVAL_MS);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleCloudliftPreviewRefresh);
} else {
  scheduleCloudliftPreviewRefresh();
}